import React, { useState } from "react";
import { Wrench, AlertTriangle, Loader2, CheckCircle2, Package } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

/**
 * Outil de réparation : détecte et purge les lots issus d'OFs supprimés ou
 * annulés mais dont les lots n'ont pas été nettoyés (cause typique : ancien
 * DELETE /api/orders avant fix Phase 49).
 *
 * Garde-fou : ne touche QUE les lots sans aucun mouvement non-IN (jamais utilisés).
 */
export default function FixOrphanLotsDialog() {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => { setPreview(null); setDone(false); };

  const runPreview = async () => {
    setLoading(true); setDone(false);
    try {
      const { data } = await api.post("/admin/fix-orphan-lots?dry_run=true");
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'aperçu");
    } finally { setLoading(false); }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post("/admin/fix-orphan-lots?dry_run=false");
      toast.success(`${data.removed_count} lot(s) fantôme(s) purgé(s)`);
      setDone(true);
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'application");
    } finally { setApplying(false); }
  };

  return (
    <AlertDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className="border-[#C05C47] text-[#C05C47] hover:bg-[#FCEEEA]"
        data-testid="open-fix-orphan-lots"
      >
        <Wrench className="w-4 h-4 mr-2" />
        Purger les lots fantômes (OFs supprimés/annulés)
      </Button>
      <AlertDialogContent className="max-w-2xl" data-testid="fix-orphan-lots-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-[#C05C47]" />
            Purger les lots fantômes
          </AlertDialogTitle>
          <AlertDialogDescription className="text-left">
            Recherche les lots actifs en stock dont l'OF d'origine a été supprimé
            (sans nettoyer les lots) ou annulé sans purge. Ces lots créent du stock
            fantôme. Seuls les lots <strong>jamais utilisés</strong> (aucun mouvement OUT)
            seront supprimés.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {!preview && !loading && (
          <div className="border border-[#E5E5E0] rounded p-4 bg-[#FAFAF7] text-sm text-[#575A57]">
            Cliquez sur « Aperçu » pour scanner les lots fantômes.
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-6 text-[#575A57]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Analyse en cours…
          </div>
        )}

        {preview && (
          <div className="space-y-3" data-testid="fix-orphan-lots-preview">
            <div className="grid grid-cols-2 gap-3">
              <div className="border border-[#E8C1B4] rounded p-3 bg-[#FCEEEA]">
                <div className="text-xs text-[#C05C47] uppercase tracking-wide">À purger</div>
                <div className="text-xl font-mono font-medium text-[#C05C47]">{preview.removed_count}</div>
              </div>
              <div className="border border-[#E8D3A8] rounded p-3 bg-[#FBF4E9]">
                <div className="text-xs text-[#8C6B2F] uppercase tracking-wide">Ignorés (déjà utilisés)</div>
                <div className="text-xl font-mono font-medium text-[#8C6B2F]">{preview.skipped_count}</div>
              </div>
            </div>

            {done && (
              <div className="border border-[#C5DBCF] bg-[#EAF4EE] rounded p-3 flex items-center gap-2 text-sm text-[#1F4E3D]">
                <CheckCircle2 className="w-4 h-4" />
                Purge appliquée. Rechargez votre vue Stocks.
              </div>
            )}

            {preview.preview && preview.preview.length > 0 && (
              <div className="border border-[#E5E5E0] rounded max-h-72 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#F4F4F0] sticky top-0">
                    <tr className="text-[#575A57]">
                      <th className="text-left p-2">N° Lot</th>
                      <th className="text-left p-2">OF origine</th>
                      <th className="text-left p-2">Statut OF</th>
                      <th className="text-right p-2">Qté</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((item) => (
                      <tr key={item.lot_id} className="border-t border-[#E5E5E0]">
                        <td className="p-2 font-mono">{item.lot_number || "—"}</td>
                        <td className="p-2 font-mono text-[10px]">{item.of_ref}</td>
                        <td className="p-2">
                          <Badge variant="outline" className="text-[10px]">{item.of_status}</Badge>
                        </td>
                        <td className="p-2 text-right font-mono">{item.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {preview.skipped && preview.skipped.length > 0 && (
              <div className="border border-[#E8D3A8] rounded p-3 bg-[#FBF4E9]/40">
                <div className="text-xs text-[#8C6B2F] font-medium mb-1 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Lots conservés (déjà utilisés en stock) — à régulariser manuellement
                </div>
                <ul className="text-[11px] text-[#5C4A1F] space-y-0.5 max-h-24 overflow-y-auto">
                  {preview.skipped.slice(0, 20).map((s) => (
                    <li key={s.lot_id}><span className="font-mono">{s.lot_number}</span> · {s.reason}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="fix-orphan-cancel">Fermer</AlertDialogCancel>
          {!preview && (
            <Button onClick={runPreview} disabled={loading} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="fix-orphan-preview-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Aperçu
            </Button>
          )}
          {preview && !done && preview.removed_count > 0 && (
            <AlertDialogAction
              onClick={apply}
              disabled={applying}
              className="bg-[#C05C47] hover:bg-[#A04634]"
              data-testid="fix-orphan-apply-btn"
            >
              {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Package className="w-4 h-4 mr-2" />}
              Purger {preview.removed_count} lot(s)
            </AlertDialogAction>
          )}
          {preview && !done && preview.removed_count === 0 && (
            <Badge className="bg-[#EAF4EE] text-[#1F4E3D] border-[#C5DBCF]">Aucun lot fantôme</Badge>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
